import { PLANT_TYPE } from '../data/plants.js';
import { TARGET_KIND, EFFECT_KIND } from '../data/disasters.js';
import { FAMILY } from '../data/families.js';
import {
  RISK_TIER,
  FLAGGED_RISK_TIERS
} from '../data/riskOutlook.js';

function pickFromTiedFamilies(families, rng) {
  if (families.length <= 1) return families[0];
  if (!rng) return [...families].sort()[0];
  const index =
    typeof rng.integer === 'function'
      ? rng.integer(0, families.length)
      : Math.floor(rng() * families.length);
  return families[index];
}

export function selectLargestFamily(portfolio, plantsById, rng, options = {}) {
  const excluded = new Set(options.excludeFamilies ?? []);
  const counts = {};
  for (const id of portfolio) {
    const family = plantsById[id]?.family;
    if (!family || excluded.has(family)) continue;
    counts[family] = (counts[family] ?? 0) + 1;
  }
  let maxCount = 0;
  for (const count of Object.values(counts)) {
    if (count > maxCount) maxCount = count;
  }
  if (maxCount === 0) return null;
  const tiedFamilies = Object.keys(counts).filter(
    (family) => counts[family] === maxCount
  );
  return pickFromTiedFamilies(tiedFamilies, rng);
}

function familyIds(portfolio, plantsById, family) {
  return [...new Set(portfolio)].filter(
    (id) => plantsById[id]?.family === family
  );
}

function largestFamilyIds(portfolio, plantsById, rng, options = {}) {
  const selectedFamily = selectLargestFamily(
    portfolio,
    plantsById,
    rng,
    options
  );
  if (!selectedFamily) return [];
  return familyIds(portfolio, plantsById, selectedFamily);
}

export function resolvePersistentDisasterTarget({
  disaster,
  portfolio,
  plantsById,
  rng = null
}) {
  if (
    disaster.effect.kind !== EFFECT_KIND.OUTPUT_MULTIPLIER ||
    !disaster.effect.persistent ||
    disaster.target.kind !== TARGET_KIND.LARGEST_FAMILY
  ) {
    return disaster;
  }
  const family = selectLargestFamily(portfolio, plantsById, rng);
  if (!family) return disaster;
  return {
    ...disaster,
    target: { kind: TARGET_KIND.FAMILY, family },
    persistentTargetFamily: family
  };
}

function targetIdsInPortfolio(
  disaster,
  portfolio,
  portfolioIdSet,
  plantsById,
  rng
) {
  const t = disaster.target;
  switch (t.kind) {
    case TARGET_KIND.PLANTS:
      return t.ids.filter((id) => portfolioIdSet.has(id));
    case TARGET_KIND.FAMILY:
      return [...portfolioIdSet].filter(
        (id) => plantsById[id].family === t.family
      );
    case TARGET_KIND.ALL_VARIABLE:
      return [...portfolioIdSet].filter(
        (id) => plantsById[id].type === PLANT_TYPE.VARIABLE
      );
    case TARGET_KIND.LARGEST_NON_NUCLEAR_FAMILY:
      return largestFamilyIds(portfolio, plantsById, rng, {
        excludeFamilies: [FAMILY.NUCLEAR]
      });
    case TARGET_KIND.LARGEST_FAMILY:
      return largestFamilyIds(portfolio, plantsById, rng);
    case TARGET_KIND.PLAYER:
    default:
      return [];
  }
}

export function resolveDisasterTargetIds({
  disaster,
  portfolio,
  plantsById,
  rng = null
}) {
  return targetIdsInPortfolio(
    disaster,
    portfolio,
    new Set(portfolio),
    plantsById,
    rng
  );
}

export function outputMultiplierFactorForPlant(effect, plantId) {
  return effect.factorsByPlantId?.[plantId] ?? effect.factor;
}

export function demandAddAmountForEffect(effect, year = null) {
  if (effect.amountByYear && year !== null && year !== undefined) {
    return effect.amountByYear[year] ?? effect.amount ?? 0;
  }
  return effect.amount ?? 0;
}

export function computeDisasterEffects({
  disasters,
  portfolio,
  plantsById,
  rng = null,
  year = null
}) {
  const portfolioIdSet = new Set(portfolio);
  const outputMultipliers = {};
  const affectedPlantIdsByDisasterId = {};
  let demandAdd = 0;
  let priceShock = false;

  for (const d of disasters) {
    if (d.effect.kind === EFFECT_KIND.OUTPUT_MULTIPLIER) {
      const ids = targetIdsInPortfolio(
        d,
        portfolio,
        portfolioIdSet,
        plantsById,
        rng
      );
      affectedPlantIdsByDisasterId[d.id] = ids;
      for (const id of ids) {
        outputMultipliers[id] =
          (outputMultipliers[id] ?? 1) *
          outputMultiplierFactorForPlant(d.effect, id);
      }
    } else if (d.effect.kind === EFFECT_KIND.OPEX_MULTIPLIER) {
      priceShock = true;
    } else if (d.effect.kind === EFFECT_KIND.DEMAND_ADD) {
      demandAdd += demandAddAmountForEffect(d.effect, year);
    }
  }

  return {
    outputMultipliers,
    affectedPlantIdsByDisasterId,
    demandAdd,
    priceShock
  };
}

export function computeFinalOutputPerType({
  portfolio,
  plantsById,
  rolledOutputs,
  outputMultipliers
}) {
  const portfolioIdSet = new Set(portfolio);
  const outputs = {};
  for (const id of portfolioIdSet) {
    const p = plantsById[id];
    const base =
      p.type === PLANT_TYPE.RELIABLE ? p.output : rolledOutputs[id];
    const mult = outputMultipliers[id] ?? 1;
    outputs[id] = base * mult;
  }
  return outputs;
}

export function sumPortfolioOutput(portfolio, outputPerType) {
  let total = 0;
  for (const id of portfolio) {
    total += outputPerType[id] ?? 0;
  }
  return total;
}

export function resolveSeverity(card, rng) {
  if (!card.twoTierSeverity) return card;
  const isSevere = rng() >= 0.5;
  const severityRoll = isSevere ? 'severe' : 'mild';
  return {
    ...card,
    effect: card.severity[severityRoll],
    severityRoll,
    isSevere
  };
}

export function assignRiskTiers(remainingPool, rng) {
  const tierByCardId = {};
  for (const card of remainingPool) {
    tierByCardId[card.id] = RISK_TIER.STANDARD;
  }
  const indices = remainingPool.map((_, i) => i);
  const slotCount = Math.min(FLAGGED_RISK_TIERS.length, indices.length);
  for (let i = 0; i < slotCount; i++) {
    const j = i + Math.floor(rng() * (indices.length - i));
    const tmp = indices[i];
    indices[i] = indices[j];
    indices[j] = tmp;
    tierByCardId[remainingPool[indices[i]].id] = FLAGGED_RISK_TIERS[i];
  }
  return tierByCardId;
}

export function tierWeightsToCardWeights({ pool, tierByCardId, tierWeights }) {
  const out = {};
  for (const card of pool) {
    const tier = tierByCardId[card.id] ?? RISK_TIER.STANDARD;
    out[card.id] = tier === RISK_TIER.STANDARD ? 1 : tierWeights[tier];
  }
  return out;
}

function pickIndexWeighted(pool, weightByCardId, rng) {
  let total = 0;
  for (const card of pool) total += weightByCardId[card.id] ?? 1;
  if (total <= 0) return Math.floor(rng() * pool.length);
  let r = rng() * total;
  for (let i = 0; i < pool.length; i++) {
    r -= weightByCardId[pool[i].id] ?? 1;
    if (r <= 0) return i;
  }
  return pool.length - 1;
}

export function drawDisasters(deck, count, rng, weightByCardId = null) {
  const remaining = [...deck];
  const drawn = [];
  for (let i = 0; i < count && remaining.length > 0; i++) {
    const idx = weightByCardId
      ? pickIndexWeighted(remaining, weightByCardId, rng)
      : Math.floor(rng() * remaining.length);
    const card = remaining[idx];
    drawn.push(resolveSeverity(card, rng));
    remaining.splice(idx, 1);
  }
  return { drawn, remaining };
}
